/**
 * V12 数据中心 — 用户上下文
 *
 * 管理当前登录用户的 openid，提供 DataProxy 所需的用户标识。
 * 职责单一：读取/设置/清除当前用户 openid。
 *
 * @see design/v12-user-data-center/spec.md §FR-001
 */

const CURRENT_USER_KEY = "battlecode-current-user";
const AUTH_TOKEN_KEY = "battlecode-auth-token";
export const AUTH_REQUIRED = "AUTH_REQUIRED";

export class AuthRequiredError extends Error {
  constructor(message = "请先登录后再保存账号数据") {
    super(message);
    this.name = AUTH_REQUIRED;
  }
}

export interface CurrentUser {
  openid: string;
  name: string;
}

/** 获取当前登录用户的 openid（null = 未登录；不进入匿名业务数据模式） */
export function getCurrentUserOpenid(): string | null {
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY);
    if (!raw) return null;
    const user: CurrentUser = JSON.parse(raw);
    return user.openid || null;
  } catch {
    return null;
  }
}

/** 获取当前用户完整信息 */
export function getCurrentUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

/** 当前关卡本地存储命名空间；未登录不得创建账号数据空间。 */
export function getLevelStorageNamespace(): string {
  const openid = getCurrentUserOpenid();
  if (!openid || !isLoggedIn()) throw new AuthRequiredError("请先登录后再访问我的关卡");
  return `user-${encodeURIComponent(openid)}`;
}

// ============================================================
// 用户切换回调钩子（供 sync-client 注册）
// ============================================================

type UserChangeHook = (openid: string | null) => Promise<void> | void;
type BeforeUserClearHook = (openid: string) => Promise<void> | void;
const userChangeHooks = new Set<UserChangeHook>();
let _onBeforeUserClear: BeforeUserClearHook | null = null;

/** 注册用户切换回调；允许多个基础设施模块独立监听。 */
export function setOnUserChange(fn: UserChangeHook | null): void {
  if (fn) userChangeHooks.add(fn);
  else userChangeHooks.clear();
}

/** 登出前钩子；用于在身份清除前完成待同步数据推送。 */
export function setOnBeforeUserClear(fn: BeforeUserClearHook | null): void {
  _onBeforeUserClear = fn;
}

/** 设置账号。deferChange 用于先完成旧账号同步，再写入新 token 后通知业务刷新。 */
export async function setCurrentUser(user: CurrentUser, options: { deferChange?: boolean } = {}): Promise<void> {
  const previous = getCurrentUserOpenid();
  if (previous && previous !== user.openid && _onBeforeUserClear) {
    await _onBeforeUserClear(previous);
  }
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
  if (!options.deferChange) await notifyUserReady();
}

/** token/profile 写入完成后触发新账号 hydrate。 */
export async function notifyUserReady(): Promise<void> {
  const openid = getCurrentUserOpenid();
  for (const hook of userChangeHooks) await hook(openid);
}

/** 清除当前用户（退出登录时调用），不会进入匿名业务存储。 */
export async function clearCurrentUser(): Promise<void> {
  const current = getCurrentUserOpenid();
  if (current && _onBeforeUserClear) await _onBeforeUserClear(current);
  localStorage.removeItem(CURRENT_USER_KEY);
  for (const hook of userChangeHooks) await hook(null);
}

/** 判断当前是否有登录用户 */
export function isLoggedIn(): boolean {
  return getCurrentUserOpenid() !== null && localStorage.getItem(AUTH_TOKEN_KEY) !== null;
}
